// The artwork details page (?art=<token>, from the confirmation email): the writer picks their image files (read
// here only, never uploaded to this site), gives each a title, medium and year, then goes on to the OneDrive upload
// with the same files. MWS Desk matches the two by file name.
import { useEffect, useRef, useState } from "react";
import type { ArtworkItem, ArtworkLookupResponse, ArtworkSaveResponse } from "./types";

type Row = ArtworkItem & { preview?: string };
const PREVIEWABLE = /^image\/(jpeg|png|gif|webp|avif)$/;
const ext = (name: string) => (name.match(/\.([A-Za-z0-9]{2,5})$/)?.[1] ?? "").toUpperCase();

export function ArtworkView({ onBack }: { onBack: () => void }) {
  const token = new URLSearchParams(window.location.search).get("art") ?? "";
  const [state, setState] = useState<ArtworkLookupResponse | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [savedAt, setSavedAt] = useState("");
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const heading = useRef<HTMLHeadingElement | null>(null);
  const picker = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const previous = document.title;
    document.title = "Your Artwork | Method Writers Speak";
    fetch("/api/artwork/lookup", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token }) })
      .then((r) => r.json() as Promise<ArtworkLookupResponse>)
      .then((d) => { setState(d); if (d.ok) { setRows(d.items); setSavedAt(d.savedAt); } })
      .catch(() => setState({ ok: false, reason: "invalid", errors: ["We couldn't reach the server. Please refresh to try again."] }));
    return () => { document.title = previous; };
  }, [token]);
  useEffect(() => { if (state) heading.current?.focus(); }, [state]);

  const addFiles = (files: FileList | null) => {
    // Copy first: clearing the input below empties the live FileList before React runs the updater.
    const picked = files ? Array.from(files) : [];
    if (!picked.length) return;
    setRows((cur) => {
      const next = [...cur];
      for (const f of picked) {
        const preview = PREVIEWABLE.test(f.type) ? URL.createObjectURL(f) : undefined;
        const at = next.findIndex((r) => r.file.toLowerCase() === f.name.toLowerCase());
        if (at >= 0) next[at] = { ...next[at]!, preview: preview ?? next[at]!.preview };
        else next.push({ file: f.name, title: "", untitled: false, medium: "", year: "", preview });
      }
      return next;
    });
    setDirty(true);
    setError(null);
    if (picker.current) picker.current.value = "";
  };
  const update = (i: number, patch: Partial<Row>) => { setRows((cur) => cur.map((r, k) => (k === i ? { ...r, ...patch } : r))); setDirty(true); };
  const remove = (i: number) => { setRows((cur) => cur.filter((_, k) => k !== i)); setDirty(true); };

  const save = async () => {
    const missing = rows.find((r) => !r.untitled && !r.title.trim());
    if (!rows.length) { setError("Choose at least one picture."); return; }
    if (missing) { setError(`Give “${missing.file}” a title, or tick Untitled.`); return; }
    setBusy(true); setError(null);
    try {
      const res = await fetch("/api/artwork/save", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, items: rows.map(({ file, title, untitled, medium, year }) => ({ file, title, untitled, medium, year })) }),
      });
      const data = (await res.json()) as ArtworkSaveResponse;
      if (data.ok) { setSavedAt(data.savedAt); setDirty(false); }
      else setError(data.errors[0] ?? "We couldn't save that. Please try again.");
    } catch {
      setError("We couldn't reach the server. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const ready = Boolean(savedAt) && !dirty && rows.length > 0;
  return (
    <main className="page proof-page">
      <div className="proof-shell">
        <div className="proof-kicker">Method Writers Speak</div>
        {!state && <p className="proof-body">Loading…</p>}
        {state && !state.ok && (
          <div className="proof-message">
            <h1 ref={heading} tabIndex={-1} className="proof-title">That link didn't work</h1>
            <p className="proof-body">{state.errors[0]}</p>
            <button type="button" className="proof-link" onClick={onBack}>Back to the main site</button>
          </div>
        )}
        {state?.ok && (
          <>
            <h1 ref={heading} tabIndex={-1} className="proof-title">Your artwork, <em>{state.first}</em></h1>
            <ol className="art-steps">
              <li className={ready ? "done" : "now"}><strong>Tell us about each piece</strong> (here)</li>
              <li className={ready ? "now" : ""}><strong>Upload the same files</strong> (the next step)</li>
            </ol>

            <section className="art-step">
              <p className="proof-body">
                Choose the pictures you'd like us to consider. They stay on your computer for now: this only reads
                their names, so we know which title goes with which file.
              </p>
              <input ref={picker} type="file" multiple accept="image/*,.tif,.tiff,.heic,.heif,.pdf" className="art-input" id="art-files"
                onChange={(e) => addFiles(e.target.files)} />
              <label htmlFor="art-files" className="proof-btn proof-btn--edit art-pick">{rows.length ? "Add more pictures" : "Choose pictures"}</label>

              {rows.length > 0 && (
                <ul className="art-list">
                  {rows.map((r, i) => (
                    <li key={r.file} className="art-row">
                      <div className="art-thumb">{r.preview ? <img src={r.preview} alt="" /> : <span>{ext(r.file) || "FILE"}</span>}</div>
                      <div className="art-fields">
                        <div className="art-file">{r.file}<button type="button" className="proof-link art-remove" onClick={() => remove(i)}>Remove</button></div>
                        <label className="proof-label">Title
                          <input className="art-text" value={r.title} disabled={r.untitled} onChange={(e) => update(i, { title: e.target.value })} placeholder="The title of this piece" />
                        </label>
                        <label className="art-check"><input type="checkbox" checked={r.untitled} onChange={(e) => update(i, { untitled: e.target.checked, title: e.target.checked ? "" : r.title })} /> Untitled</label>
                        <div className="art-two">
                          <label className="proof-label">Medium <span className="proof-hint">(optional)</span>
                            <input className="art-text" value={r.medium} onChange={(e) => update(i, { medium: e.target.value })} placeholder="e.g. Oil on canvas, photograph, ink" />
                          </label>
                          <label className="proof-label">Year <span className="proof-hint">(optional)</span>
                            <input className="art-text art-year" value={r.year} inputMode="numeric" onChange={(e) => update(i, { year: e.target.value })} placeholder="e.g. 2024" />
                          </label>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              {error && <p className="proof-error" role="alert">{error}</p>}
              {rows.length > 0 && (
                <div className="proof-actions">
                  <button type="button" className="proof-btn proof-btn--ok" disabled={busy || ready} onClick={() => void save()}>
                    {busy ? "Saving…" : ready ? "Saved" : "Save and continue"}
                  </button>
                </div>
              )}
            </section>

            {ready && (
              <section className="art-step art-upload" aria-live="polite">
                <h2 className="proof-subtitle">Now upload these files</h2>
                <ol className="art-howto">
                  <li>Press the button below. It opens our upload page (Microsoft OneDrive) in a new tab.</li>
                  <li>When it asks for your name, type <strong>{state.name}</strong>, the name you submitted under.</li>
                  <li>Choose the <strong>same files</strong>, without renaming them:
                    <ul className="art-names">{rows.map((r) => <li key={r.file}>{r.file}</li>)}</ul>
                  </li>
                </ol>
                <p><a className="proof-btn proof-btn--ok art-go" href={state.uploadUrl} target="_blank" rel="noreferrer">Upload my files</a></p>
                <p className="proof-hint">Send the largest, highest-quality files you have. Not all artwork is accepted, but we always love seeing what writers bring.</p>
              </section>
            )}
            <p className="proof-exit"><button type="button" className="proof-link" onClick={onBack}>← Back to the main site</button></p>
          </>
        )}
      </div>
    </main>
  );
}
